import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  EnvelopeSimple, 
  ArrowsClockwise, 
  Copy,
  CheckCircle,
  PaperPlaneTilt,
  Gift,
  Users,
  ShoppingCart,
  Megaphone,
  Sparkle
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import IdeaSuggester from '../components/IdeaSuggester';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShareResultButton } from '../components/ShareResultButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';
import { usePersistedInput, usePersistedOutput } from '../hooks/usePersistedState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const campaignTypes = [
  { id: 'welcome', label: 'Welcome Email', icon: Sparkle, description: 'New subscriber welcome' },
  { id: 'launch', label: 'Product Launch', icon: Megaphone, description: 'Launch announcement' },
  { id: 'promo', label: 'Promotional', icon: Gift, description: 'Sales & offers' },
  { id: 'reengagement', label: 'Re-engagement', icon: Users, description: 'Win back subscribers' },
  { id: 'abandoned_cart', label: 'Abandoned Cart', icon: ShoppingCart, description: 'Cart recovery' },
  { id: 'newsletter', label: 'Newsletter', icon: EnvelopeSimple, description: 'Regular updates' }
];

const tones = ['Professional', 'Casual', 'Witty', 'Inspirational', 'Urgent', 'Friendly'];

export const EmailPage = () => {
  const [selectedType, setSelectedType] = usePersistedInput('email', 'selectedType', null);
  const [goal, setGoal] = usePersistedInput('email', 'goal', '');
  const [offer, setOffer] = usePersistedInput('email', 'offer', '');
  const [audience, setAudience] = usePersistedInput('email', 'audience', '');
  const [tone, setTone] = usePersistedInput('email', 'tone', '');
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = usePersistedOutput('email', null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('create');
  // Zero Friction: Customise → progressive disclosure for Offer/Audience/Tone
  const [showAdvanced, setShowAdvanced] = useState(false);
  // BrandSettingsSummary onLoad keeps a copy of the user's industry for auto-suggest
  const [brandProfile, setBrandProfile] = useState(null);

  // "Not sure? →" auto-suggest a contextual goal based on campaign type + brand
  const handleSuggestGoal = () => {
    if (!selectedType) return;
    const brand = brandProfile?.brand_name?.trim() || 'your brand';
    const industry = brandProfile?.industry?.trim() || '';
    const suggestions = {
      welcome:        `Welcome new subscribers and introduce ${brand}`,
      launch:         `Announce ${brand}'s new product${industry ? ' for ' + industry + ' buyers' : ''} and drive first purchases`,
      promo:          `Drive sales with a time-limited offer`,
      reengagement:   `Win back subscribers who haven't opened in 30 days`,
      abandoned_cart: `Recover abandoned purchases with a friendly reminder`,
      newsletter:     `Share ${brand}'s latest updates and insights`,
    };
    const next = suggestions[selectedType];
    if (next) setGoal(next);
  };

  const handleHistoryReload = (item) => {
    const inputs = item.inputs || {};
    if (inputs.campaign_type) setSelectedType(inputs.campaign_type);
    if (inputs.goal) setGoal(inputs.goal);
    if (inputs.offer) setOffer(inputs.offer);
    if (inputs.audience) setAudience(inputs.audience);
    if (inputs.tone) setTone(inputs.tone);
    const output = item.output || {};
    if (output.email) setEmailData(output.email);
    setTab('create');
  };

  useEffect(() => {
    // BrandSettingsSummary will also fetch /api/user/profile; we only need
    // to seed the local state for the tone/audience pre-fill on first load.
    const prefill = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user/profile`);
        const bp = data.brand_profile;
        if (bp) {
          setBrandProfile(bp);
          if (bp.target_audience) setAudience(prev => prev || bp.target_audience);
          if (bp.tone_of_voice)   setTone(prev => prev || bp.tone_of_voice);
        }
      } catch { /* ignore */ }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateEmail = async () => {
    if (!selectedType || !goal.trim()) {
      setError('Add a goal — or tap "Not sure? →" and we\'ll suggest one.');
      return;
    }
    setError('');
    setLoading(true);
    setEmailData(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/email/generate`, {
        campaign_type: selectedType,
        goal,
        offer: offer || 'Value delivery',
        audience: audience || (brandProfile?.target_audience || 'general audience'),
        tone: tone || (brandProfile?.tone_of_voice || 'Friendly'),
      });

      setEmailData(data.email);
    } catch (err) {
      console.error('Failed to generate email:', err);
      setError('Could not write the email just now. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <DashboardLayout>
      <div data-testid="email-page" className="space-y-8">
        <BrandSettingsSummary fields={['brand','tone','audience']} />
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <EnvelopeSimple size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Email Campaign Assistant</h1>
              <p className="text-zinc-400 text-sm">Email campaigns — written and ready to send</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Campaign Type Selection */}
        {tab === 'create' && !selectedType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Choose Campaign Type</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaignTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    data-testid={`campaign-${type.id}`}
                    className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 text-left hover:bg-zinc-900/80 hover:border-white/20 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center mb-4 group-hover:bg-white/10 transition-colors">
                      <Icon size={20} className="text-white" />
                    </div>
                    <h3 className="font-medium mb-1">{type.label}</h3>
                    <p className="text-sm">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Generate Form */}
        {tab === 'create' && selectedType && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5 sticky top-24">
                <div className="flex items-center justify-between">
                  <h2 className="font-medium font-['Instrument_Serif',serif]">
                    {campaignTypes.find(t => t.id === selectedType)?.label}
                  </h2>
                  <button
                    onClick={() => { setSelectedType(null); setEmailData(null); setShowAdvanced(false); }}
                    className="text-zinc-400 hover:text-white text-sm"
                    data-testid="change-campaign-type"
                  >
                    Change →
                  </button>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[11px] uppercase tracking-wider font-medium" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>What's the goal?</label>
                    <button onClick={handleSuggestGoal} data-testid="suggest-goal-btn"
                      className="text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: '#c8f060', fontFamily: 'ui-monospace, monospace' }}>
                      Not sure? →
                    </button>
                  </div>
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    data-testid="goal-input"
                    placeholder="e.g., Drive 50 purchases this week"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>

                {error && (
                  <div data-testid="email-error" className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">{error}</div>
                )}

                <button
                  onClick={generateEmail}
                  disabled={loading || !goal}
                  data-testid="generate-email-btn"
                  className="w-full rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  style={{ background: '#c8f060', color: '#080808' }}
                >
                  {loading ? (
                    <>
                      <ArrowsClockwise size={18} className="animate-spin" />
                      Writing your email…
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={18} weight="fill" />
                      Write my email
                    </>
                  )}
                </button>

                <button onClick={() => setShowAdvanced(v => !v)} data-testid="email-customise-toggle"
                  className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {showAdvanced ? 'Hide customise' : 'Customise →'}
                  </span>
                  <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
                </button>

                {showAdvanced && (
                  <div className="space-y-4 pt-1">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Offer (optional)</label>
                      <input
                        type="text"
                        value={offer}
                        onChange={(e) => setOffer(e.target.value)}
                        placeholder="e.g., 20% off, free shipping"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Audience override</label>
                      <input
                        type="text"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        data-testid="audience-input"
                        placeholder="e.g., Previous customers, new subscribers"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Tone override</label>
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger data-testid="tone-select" className="w-full bg-zinc-900 border-white/10 text-white">
                          <SelectValue placeholder="Select tone" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          {tones.map((t) => (
                            <SelectItem key={t} value={t} className="text-white hover:bg-white/10">
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="pt-1">
                      <IdeaSuggester creator="email" compact label="Suggest goal ideas"
                        onPick={(idea) => {
                          if (idea.angle) setGoal(idea.angle);
                          else if (idea.subject) setGoal(idea.subject);
                          if (idea.offer) setOffer(idea.offer);
                        }} />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 space-y-6"
            >
              {!emailData ? (
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="text-center py-16">
                    <EnvelopeSimple size={48} className="mx-auto mb-4 text-zinc-700" />
                    <h3 className="text-lg font-medium mb-2">No email generated yet</h3>
                    <p className="text-sm">Fill in your details and generate your email</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Subject Lines */}
                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Subject Line Options</h2>
                      <ShareResultButton
                        title="Email Campaign"
                        content={`Subject: ${emailData.subject_lines?.[0] || ''}\n\n${emailData.body?.slice(0, 300) || ''}`}
                        type="Email Campaign"
                      />
                    </div>
                    <div className="space-y-3">
                      {emailData.subject_lines?.map((line, index) => (
                        <div key={index} className="flex items-center justify-between gap-4 p-3 bg-zinc-800/50 rounded-xl">
                          <span className="text-zinc-200">{line}</span>
                          <button
                            onClick={() => copyToClipboard(line, `subject-${index}`)}
                            className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            {copiedKey === `subject-${index}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview Text */}
                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Preview Text Options</h2>
                    <div className="space-y-3">
                      {emailData.preview_texts?.map((text, index) => (
                        <div key={index} className="flex items-center justify-between gap-4 p-3 bg-zinc-800/50 rounded-xl">
                          <span className="text-zinc-300 text-sm">{text}</span>
                          <button
                            onClick={() => copyToClipboard(text, `preview-${index}`)}
                            className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            {copiedKey === `preview-${index}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Email Body */}
                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Email Body</h2>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            try {
                              const subj = emailData.subject_lines?.[0] || 'Your email';
                              const { data } = await axios.post(`${API_URL}/api/email/render`, {
                                subject: subj,
                                body: emailData.email_body,
                                cta: emailData.cta_text || 'Get Started',
                              });
                              const w = window.open('', '_blank');
                              if (w) { w.document.write(data.html); w.document.close(); }
                            } catch { /* no-op */ }
                          }}
                          data-testid="email-preview-btn"
                          className="px-3 py-1.5 text-xs font-medium rounded-lg" style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.18)' }}>
                          Preview as inbox
                        </button>
                        <a
                          href="#"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const subj = emailData.subject_lines?.[0] || 'Your email';
                              const resp = await axios.post(`${API_URL}/api/email/download`, {
                                subject: subj,
                                body: emailData.email_body,
                                cta: emailData.cta_text || 'Get Started',
                                format: 'html',
                              }, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/html' }));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${(subj || 'email').replace(/[^a-z0-9-]/gi, '_').slice(0, 40)}.html`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            } catch { /* no-op */ }
                          }}
                          data-testid="email-download-btn"
                          className="px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer"
                          style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.08)' }}>
                          Download HTML
                        </a>
                        <button
                          onClick={() => copyToClipboard(emailData.email_body, 'body')}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copiedKey === 'body' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                        </button>
                      </div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-xl p-4">
                      <pre className="text-zinc-200 text-sm whitespace-pre-wrap font-sans">{emailData.email_body}</pre>
                    </div>
                  </div>

                  {/* CTA & Strategy */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                      <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Suggested CTA</h2>
                      <div className="flex items-center justify-between gap-4 p-3 bg-white/10 rounded-xl">
                        <span className="text-white font-medium">{emailData.cta_text}</span>
                        <button
                          onClick={() => copyToClipboard(emailData.cta_text, 'cta')}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copiedKey === 'cta' ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                      <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Send Strategy</h2>
                      <p className="text-zinc-400 text-sm">{emailData.send_strategy}</p>
                    </div>
                  </div>

                  {/* Regenerate */}
                  <button
                    onClick={generateEmail}
                    disabled={loading}
                    className="w-full py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
                    Regenerate Email
                  </button>
                </>
              )}
            </motion.div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Email History</h2>
            <TextHistoryPanel toolType="email" onReload={handleHistoryReload} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmailPage;
